import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import NotificationSettings from "./NotificationSettings";
import { setupNotifications } from "../lib/notifications";
import { getPrayerTimes } from "../lib/prayerTimesAPI";
import { useToast } from "./ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [location, setLocation] = useState("Stockholm, Sverige");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState(15);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [nextPrayer, setNextPrayer] = useState("maghrib");

  // Hämta inställningarna vid laddning
  useEffect(() => {
    // Hämta sparade notifikationsinställningar från localStorage
    const savedSettings = localStorage.getItem("notificationSettings");
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setNotificationsEnabled(settings.enabled || false);
      setReminderTime(settings.reminderTime || 15);
      setSoundEnabled(settings.soundEnabled || false);
    }
  }, []);

  // Hantera sparade inställningar
  const handleSaveSettings = async (notificationsEnabled: boolean, reminderTime: number) => {
    try {
      // Save settings to localStorage to ensure they're available to other components
      const settings = {
        enabled: notificationsEnabled,
        reminderTime: reminderTime,
        soundEnabled: false // Add sound option if needed
      };
      localStorage.setItem('notificationSettings', JSON.stringify(settings));
      
      if (notificationsEnabled) {
        // Om notifikationer är aktiverade, konfigurera dem
        if (Notification.permission !== "granted") {
          await Notification.requestPermission();
        }
        
        if (Notification.permission === "granted") {
          const prayerTimes = await getPrayerTimes(location);
          setupNotifications(prayerTimes);
          
          toast({
            title: "Inställningar sparade",
            description: `Påminnelser aktiverade ${reminderTime} minuter innan iftar.`,
          });
        } else {
          toast({
            title: "Notifikationer blockerade",
            description: "Du måste tillåta notifikationer i webbläsaren.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Inställningar sparade",
          description: "Påminnelser inaktiverade.",
        });
      }
      
      // Navigera tillbaka efter en kort fördröjning
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          navigate("/");
          resolve();
        }, 1000);
      });
    } catch (error) {
      console.error("Kunde inte spara inställningar:", error);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte spara inställningarna.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <div className="container max-w-md mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Inställningar</h1>
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Påminnelser</CardTitle>
          </CardHeader>
          <CardContent>
            <NotificationSettings
              enabled={notificationsEnabled}
              minutesBefore={reminderTime}
              soundEnabled={soundEnabled}
              nextPrayer={nextPrayer}
              onSave={async (enabled, minutesBefore, soundEnabled) => {
                setNotificationsEnabled(enabled);
                setReminderTime(minutesBefore);
                setSoundEnabled(soundEnabled);
                await handleSaveSettings(enabled, minutesBefore);
              }}
            />
          </CardContent>
        </Card>
        
        <Button variant="outline" className="w-full" onClick={() => navigate("/")}>
          Tillbaka
        </Button>
      </div>
    </div>
  );
}